import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled, { keyframes } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { HorizontalGlitch } from '@sorare/core/src/atoms/animations/HorizontalGlitch';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePrevious } from '@sorare/core/src/hooks/usePrevious';
import { getRewardRGBColor } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { absCenter } from '@sorare/core/src/lib/style';

import { AlternativeRewardButton } from '#src/components/so5/LastFixtureStory/CardRewardsReveal/AlternativeRewardButton/index.tsx';
import { RevealableReward } from '#src/components/so5/LastFixtureStory/CardRewardsReveal/RevealableReward/index.tsx';
import { RewardSwitch } from '#src/components/so5/LastFixtureStory/CardRewardsReveal/RewardSwitch/index.tsx';
import { useClaimRewards } from '#src/hooks/so5/useClaimRewards.ts';

import type {
  RewardWithAlternaltiveReward_anyRewardConfigInterface,
  RewardWithAlternaltiveReward_so5Reward,
} from './__generated__/index.graphql.ts';

const fadeTranslateIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const RevealableRewardWrapper = styled.div`
  z-index: 1;
`;
const AlternativeReward = styled.div`
  position: relative;

  opacity: 0;
  &.revealed {
    animation: 300ms ${fadeTranslateIn} ease-out forwards;
  }
`;
const HorizontalGlitchWrapper = styled(Horizontal)`
  padding-bottom: var(--triple-unit);
`;
const BackgroundGlare = styled.div`
  --bg-rgb-color: 255, 0, 0;

  ${absCenter}
  z-index: -1;
  top: -40px;
  width: 100%;
  height: 300%;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgb(var(--bg-rgb-color)),
    transparent
  );
`;

type Props = {
  so5Reward: RewardWithAlternaltiveReward_so5Reward;
  selectedSignedAlternativeRewardConfigs?: string | null;
  onAlternativeRewardClick?: (signedAlternativeRewardConfigs: string) => void;
  onReveal?: (so5RewardId: string) => void;
  disabledClaim?: boolean;
};
export const RewardWithAlternaltiveReward = ({
  so5Reward,
  selectedSignedAlternativeRewardConfigs,
  onAlternativeRewardClick,
  onReveal: onRevealProp,
  disabledClaim,
}: Props) => {
  const [glitching, setGlitching] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [claim] = useClaimRewards();
  const { sport } = useSportContext();

  const { id, rewards, rewardConfigs, alternativeRewardConfigs } =
    so5Reward || {};
  const firstReward = rewards.at(0);
  const selectedAlternativeReward = alternativeRewardConfigs?.find(
    ({ signedAlternativeRewardConfigs }) =>
      !!selectedSignedAlternativeRewardConfigs &&
      selectedSignedAlternativeRewardConfigs === signedAlternativeRewardConfigs
  );

  const onReveal = () => {
    if (isType(firstReward, 'AnyCardReward') && !disabledClaim) {
      claim({ so5RewardIds: [id] });
    } else if (
      isType(firstReward, 'CardShardsReward') ||
      isType(firstReward, 'MonetaryReward')
    ) {
      setRevealed(true);
    }
    onRevealProp?.(so5Reward.id);
  };

  const firstAlternativeChoice = (
    selectedAlternativeReward
      ? rewardConfigs
      : alternativeRewardConfigs?.at(0)?.rewardConfigs || []
  ).at(0);
  const rgbColor = firstAlternativeChoice
    ? getRewardRGBColor(firstAlternativeChoice)
    : '31, 18, 28';

  const rewardConfig = (
    selectedAlternativeReward?.rewardConfigs || so5Reward.rewardConfigs
  ).at(0);

  const previousRewardConfig = usePrevious(rewardConfig);

  return (
    <Vertical gap={2}>
      <RevealableRewardWrapper>
        <RewardSwitch
          rewardConfig={rewardConfig}
          previousRewardConfig={previousRewardConfig}
          revealed={revealed}
          rgbColor={rewardConfig ? getRewardRGBColor(rewardConfig) : null}
        >
          {({ config }) => {
            const leaguePictureUrl =
              sport === Sport.NBA
                ? so5Reward.so5Leaderboard.so5LeaderboardGroup.headerUrl
                : so5Reward.so5Leaderboard.so5League.headerUrl;

            return (
              <RevealableReward
                key={so5Reward.slug}
                so5Reward={so5Reward}
                rewardConfig={config}
                leaguePictureUrl={leaguePictureUrl}
                onReveal={onReveal}
                revealed={revealed}
              />
            );
          }}
        </RewardSwitch>
      </RevealableRewardWrapper>
      <AlternativeReward className={classNames({ revealed })}>
        <BackgroundGlare
          style={{ '--bg-rgb-color': rgbColor } as CSSProperties}
        />
        {so5Reward.alternativeRewardConfigs?.map(
          (alternativeRewardConfig, i) => {
            const alternativeRewardSelected =
              selectedAlternativeReward?.signedAlternativeRewardConfigs ===
              alternativeRewardConfig.signedAlternativeRewardConfigs;

            return (
              // eslint-disable-next-line react/no-array-index-key
              <HorizontalGlitchWrapper key={i} center>
                <BrandXS color="var(--c-nd-700)">
                  <FormattedMessage {...glossary.or} />
                </BrandXS>
                <HorizontalGlitch
                  bgRgbColor={getRewardRGBColor(
                    (alternativeRewardSelected
                      ? rewardConfigs
                      : alternativeRewardConfig.rewardConfigs)[0]
                  )}
                  glitching={glitching}
                >
                  <AlternativeRewardButton
                    anyRewardConfigs={
                      alternativeRewardSelected
                        ? rewardConfigs
                        : alternativeRewardConfig.rewardConfigs
                    }
                    onClick={() => {
                      if (!glitching) {
                        setGlitching(true);
                        onAlternativeRewardClick?.(
                          alternativeRewardConfig.signedAlternativeRewardConfigs
                        );
                        setTimeout(() => setGlitching(false), 1500);
                      }
                    }}
                  />
                </HorizontalGlitch>
              </HorizontalGlitchWrapper>
            );
          }
        )}
      </AlternativeReward>
    </Vertical>
  );
};

const anyRewardConfigInterfaceFragment = gql`
  fragment RewardWithAlternaltiveReward_anyRewardConfigInterface on AnyRewardConfigInterface {
    id
    ...getRewardRGBColor_anyRewardConfigInterface
    ...AlternativeRewardButton_anyRewardConfigInterface
    ...RevealableReward_anyRewardConfigInterface
    ...getRewardRGBColor_anyRewardConfigInterface
  }
  ${AlternativeRewardButton.fragments.anyRewardConfigInterface}
  ${RevealableReward.fragments.anyRewardConfigInterface}
  ${getRewardRGBColor.fragments.anyRewardConfigInterface}
  ${getRewardRGBColor.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<RewardWithAlternaltiveReward_anyRewardConfigInterface>;

RewardWithAlternaltiveReward.fragments = {
  so5Reward: gql`
    fragment RewardWithAlternaltiveReward_so5Reward on So5Reward {
      slug
      id
      rewards {
        id
      }
      so5Leaderboard {
        slug
        so5LeaderboardGroup(groupType: COMPETITION) {
          slug
          headerUrl
        }
        so5League {
          slug
          headerUrl
        }
      }
      alternativeRewardConfigs {
        signedAlternativeRewardConfigs
        rewardConfigs {
          id
          ...RewardWithAlternaltiveReward_anyRewardConfigInterface
        }
      }
      rewardConfigs {
        id
        ...RewardWithAlternaltiveReward_anyRewardConfigInterface
      }
      ...RevealableReward_so5Reward
    }
    ${RevealableReward.fragments.so5Reward}
    ${anyRewardConfigInterfaceFragment}
  ` as TypedDocumentNode<RewardWithAlternaltiveReward_so5Reward>,
};
